import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRegisterLightweightUserMutation } from "../../../../../redux/api/ev-charger/evChargerAPI";
import RegisterLightweightUserRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/register-lightweight-user-request-dto";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppInput from "../../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";

interface IFormInput {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

const defaultFormValues: IFormInput = {
  firstName: "",
  lastName: "",
  email: "",
  mobileNumber: "",
};

interface IProps extends ModelContainerProps {
  show: boolean;
}

const AddUserModal = (props: IProps) => {
  const { show, onClose, onCancel, ...rest } = props;

  const [registerLightweightUser, { isLoading }] =
    useRegisterLightweightUserMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  useEffect(() => {
    if (!show) {
      reset({ ...defaultFormValues });
    }
  }, [show, reset]);

  const handleResetAndClose = () => {
    reset({ ...defaultFormValues });
    if (onClose) onClose();
    if (onCancel) onCancel();
  };

  const onSubmit = (data: IFormInput) => {
    const payload: RegisterLightweightUserRequestDTO = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
      mobileNumber: data.mobileNumber ? data.mobileNumber.trim() : "",
    };

    registerLightweightUser(payload)
      .unwrap()
      .then(() => {
        showSuccessMessage("User added successfully");
        handleResetAndClose();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err.message || "Failed to add user");
      });
  };

  return (
    <ModalContainer
      show={show}
      {...rest}
      onClose={handleResetAndClose}
      onCancel={handleResetAndClose}
      onConfirm={handleSubmit(onSubmit)}
      title="Add User"
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      size="modal-md"
      isLoading={isLoading}
    >
      <>
        <AppInput
          label="First Name"
          placeholder="Enter First Name"
          name="firstName"
          register={register("firstName", {
            required: "First Name is required",
          })}
          errors={errors}
        />
        <AppInput
          className="mt-3"
          label="Last Name"
          placeholder="Enter Last Name"
          name="lastName"
          register={register("lastName", {
            required: "Last Name is required",
          })}
          errors={errors}
        />
        <AppInput
          className="mt-3"
          label="Email"
          placeholder="Enter Email"
          name="email"
          register={register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          errors={errors}
        />
        <AppInput
          className="mt-3"
          label="Mobile Number"
          placeholder="Enter Mobile Number"
          name="mobileNumber"
          register={register("mobileNumber")}
          errors={errors}
          isRequired={false}
        />
      </>
    </ModalContainer>
  );
};

export default AddUserModal;
